import { useState } from "react";
import type { CustomerDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";

interface Line {
  description: string;
  quantity: string;
  unitPrice: string;
}

export function InvoiceDialog({
  open,
  onOpenChange,
  customers,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  customers: CustomerDto[];
  onCreated(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<Line[]>([
    { description: "", quantity: "1", unitPrice: "" },
  ]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.createInvoice({
        customerId,
        notes,
        lines: lines.filter((l) => l.description.trim() && l.unitPrice),
      });
      push(t("finance.invoiceCreatedToast"), res.invoice.number);
      setLines([{ description: "", quantity: "1", unitPrice: "" }]);
      setNotes("");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("finance.newInvoice")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={
              busy ||
              !customerId ||
              !lines.some((l) => l.description.trim() && l.unitPrice)
            }
          >
            {t("finance.createInvoice")}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pe-1">
        <SelectInput
          label={t("common.fields.customer")}
          options={customers.map((c) => ({
            value: c.id,
            label: c.displayName || c.legalName,
          }))}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        />
        {lines.map((line, idx) => (
          <div
            key={idx}
            className="grid items-end gap-2 rounded border border-line p-2 sm:grid-cols-3"
          >
            <TextInput
              label={t("common.fields.description")}
              value={line.description}
              onChange={(e) =>
                setLines((l) =>
                  l.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x))
                )
              }
            />
            <TextInput
              label={t("common.fields.quantity")}
              value={line.quantity}
              onChange={(e) =>
                setLines((l) =>
                  l.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x))
                )
              }
            />
            <TextInput
              label={t("common.fields.unitPrice")}
              value={line.unitPrice}
              onChange={(e) =>
                setLines((l) =>
                  l.map((x, i) => (i === idx ? { ...x, unitPrice: e.target.value } : x))
                )
              }
            />
          </div>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            setLines((l) => [...l, { description: "", quantity: "1", unitPrice: "" }])
          }
        >
          {t("finance.addLine")}
        </Button>
        <TextInput
          label={t("common.fields.notes")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}
